import React, { useEffect } from 'react';
import {
  TAB_JOBDETAIL,
  TAB_PR,
  TAB_PHOTOS,
} from '@doda/common/src/constants/properties/const';
import { createTabUrl } from '@doda/common/src/logics/jobSearchDetail/createTabUrl';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  queryParamsSelector,
  setQueryParams,
} from '@doda/common/src/redux/features/queryParams';
import { canonicalUrlSelector } from '@doda/common/src/redux/features/canonical';
import { useViewCount } from '@doda/common/src/hooks/useViewCount';
import {
  measureAbTestForManuscriptEMovePickUp,
  measureChangeTabState,
  measureClickTabPickup,
  measureView2timesMoreJobSearchDetail,
} from '@doda/common/src/logics/jobSearchDetail/analytics';
import { loginStatusSelector } from '@doda/common/src/redux/features/memberInfo';
import { PAGES } from '@doda/common/src/constants/pages';
import { tabSelector } from 'redux/selectors/queryParams';
import { windowScrollTop } from '@doda/common/src/utils/scrollUtil';
import {
  abTestForManuscriptEGroupNoSelector,
  ABTestForManuscriptEPattern,
} from '@doda/common/src/redux/features/abTest';
import { Tab } from '../../../types/tab';
import {
  isViewedJobDetailTabSelector,
  isViewedPickUpTabSelector,
  viewedJobDetailTab,
  viewedPickUpTab,
  corporateClientPhotoSelector,
} from '../../../redux/features/jobSearchDetail';

const TAB_STATE = {
  pr: 'pr',
  jd: 'jobdetail',
  photos: 'photos',
  photos_pc: 'photos_pc',
} as const;

/**
 * TabItemType
 */
export interface TabItemType {
  /** tabのhrefに設定するリンク */
  href: string;
  /** tabを押下した時の処理 */
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /** tabの表示名 */
  name: string;
  /** tabの選択状態 */
  isSelected: boolean;
}

/**
 * 求人詳細とPRと写真タブのURL、クリックイベントハンドラ、およびタブの状態を提供するカスタムフック。
 * 初期タブ、求人ID、wcnsId及び現在選択されているタブ名をもとに、タブの情報を提供する。
 *
 * @param {object} props - プロパティオブジェクト
 * @param {Tab} props.initialTab - コンポーネントの初期状態でアクティブなタブ
 * @param {string} props.jid - 求人ID
 * @param {string} props.wcnsId - 原稿種別
 * @param {boolean} props.applied - 応募済みか
 * @returns {object} - タブのアイテム配列を含むオブジェクト
 * @property {function} createTabItems - タブのアイテムを生成する関数
 * @property {function} onClickTab - タブをクリックしたときの処理
 * @property {Tab} currentTabName - 現在の選択されているタブの名前
 * @property {function} onChangeTab - タブを変更する処理
 */
export const useTab = ({
  initialTab,
  jid,
  wcnsId,
}: {
  initialTab: Tab;
  jid: string;
  wcnsId: string;
}) => {
  const dispatch = useAppDispatch();
  const isLogin = useAppSelector(loginStatusSelector);
  const { addCount, getCount } = useViewCount();
  const queryParams = useAppSelector(queryParamsSelector);
  const canonicalUrl = useAppSelector(canonicalUrlSelector) ?? '';
  const baseJobDetailUrl = canonicalUrl.replace(/-tab__[^/]*\//, '/');
  const currentTabName = useAppSelector(tabSelector) || initialTab;
  const isViewedJobDetailTab = useAppSelector(isViewedJobDetailTabSelector);
  const isViewedPickUpTab = useAppSelector(isViewedPickUpTabSelector);
  const abTestForManuscriptEGroupNo = useAppSelector(
    abTestForManuscriptEGroupNoSelector
  );
  const publicationPhotos = useAppSelector(corporateClientPhotoSelector);

  /**
   * タブを変更する
   * @param {Tab} tabName - 変更するタブの名前
   * @param {boolean} isApplied - 応募済みか
   */
  const onChangeTab = (tabName: Tab, isApplied: boolean) => {
    // 初期のタブがPRで求人詳細タブに移動した場合、1回のみviewCountを更新する。
    if (!isApplied && !isViewedJobDetailTab) {
      dispatch(viewedJobDetailTab());
      addCount(jid);
      const viewCount = getCount(jid);
      if (viewCount) measureView2timesMoreJobSearchDetail(viewCount.count);
    }
    if (
      !isViewedPickUpTab &&
      abTestForManuscriptEGroupNo === ABTestForManuscriptEPattern.A
    ) {
      measureAbTestForManuscriptEMovePickUp();
      dispatch(viewedPickUpTab());
    }
    let tabUrl: string | undefined;
    if (tabName === 'photos') {
      tabUrl = createTabUrl(queryParams, TAB_STATE.photos_pc);
    } else {
      tabUrl = createTabUrl(queryParams, TAB_STATE[tabName]);
    }
    dispatch(setQueryParams({ ...queryParams, tab: tabName }));
    windowScrollTop();
    window.history.pushState(null, '', tabUrl);
  };

  /**
   * タブをクリックする
   * @param {Tab} tabName - クリックされたタブの名前
   * @param {React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>} event - クリックイベント
   * @param isApplied - 応募済みか
   * @returns タブ押下時のイベント
   */
  const onClickTab =
    (tabName: Tab, isApplied: boolean) =>
    (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      event.preventDefault();
      // 現在のタブの場合は、処理をskipする。
      if (queryParams?.tab === tabName) return;
      measureChangeTabState(PAGES.JOB_SEARCH_DETAIL, isLogin);
      // pickup押下時の計測
      if (tabName === TAB_PR) {
        measureClickTabPickup(PAGES.JOB_SEARCH_DETAIL, isLogin);
      }
      onChangeTab(tabName, isApplied);
      if (tabName === TAB_PHOTOS) onChangeTab(tabName, isApplied);
    };

  /**
   * タブを生成する
   * @param {boolean} isApplied - 応募済みか
   * @returns - タブ
   */
  const createTabItems = (isApplied: boolean) => [
    ...(wcnsId === 'E' &&
    abTestForManuscriptEGroupNo !== ABTestForManuscriptEPattern.B
      ? [
          {
            name: 'Pick up!',
            href: `${baseJobDetailUrl}/-tab__${TAB_PR}/`,
            onClick: onClickTab(TAB_PR, isApplied),
            isSelected: currentTabName === TAB_PR,
          },
        ]
      : []),
    {
      name: '求人詳細',
      href: `${baseJobDetailUrl}/-tab__${TAB_JOBDETAIL}/`,
      onClick: onClickTab(TAB_JOBDETAIL, isApplied),
      isSelected: currentTabName === TAB_JOBDETAIL,
    },
    ...(['T', 'A', 'B', 'C', 'D', 'E'].includes(wcnsId) &&
    publicationPhotos &&
    publicationPhotos.length > 0
      ? [
          {
            name: '写真',
            href: `${baseJobDetailUrl}/-tab__${TAB_PHOTOS}/`,
            onClick: onClickTab(TAB_PHOTOS, isApplied),
            isSelected: currentTabName === TAB_PHOTOS,
          },
        ]
      : []),
  ];

  return { createTabItems, onClickTab, currentTabName, onChangeTab };
};

/**
 * 初期表示時にviewCountを更新する
 * @param {Tab} props.initialTab - コンポーネントの初期状態でアクティブなタブ
 * @param {string} props.jid - 求人ID
 * @param {boolean} props.isApplied - 応募済みか
 */
export const useCountView = ({
  initialTab,
  jid,
  isApplied,
}: {
  initialTab: Tab;
  jid: string;
  isApplied: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { addCount, getCount } = useViewCount();
  const isViewedJobDetailTab = useAppSelector(isViewedJobDetailTabSelector);
  const isViewedPickUpTab = useAppSelector(isViewedPickUpTabSelector);

  // viewCount更新
  useEffect(() => {
    if (isApplied) return;
    if (!isViewedJobDetailTab && initialTab === TAB_JOBDETAIL) {
      dispatch(viewedJobDetailTab());
      addCount(jid);
      const viewCount = getCount(jid);
      if (viewCount) measureView2timesMoreJobSearchDetail(viewCount.count);
    }
    if (!isViewedPickUpTab && initialTab === TAB_PR) {
      dispatch(viewedPickUpTab());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewedJobDetailTab, isViewedPickUpTab]);
};
